import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'motion/react';
import { useConfirm } from '../common/ConfirmDialog';
import PolicyGeneratorLink from '../common/PolicyGeneratorLink';
import {
  Alert,
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Policy as PolicyIcon,
  Code as CodeIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Link as LinkIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { debounce } from 'lodash';
import { filterInput } from '../../utils/inputSanitization';
import { getCustomerIdFromSubdomain } from '../../utils/constants';
import { previewPolicyJson } from '../../utils/policyJson';
import { CK, FONT_BODY, emptyTextSx, statusChipSx } from '../../pages/portal/portalStyles';

const containerVariants = {
  initial: {},
  animate: {
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
};

// Helper function to count non-empty inline policies
const countInlinePolicies = (inlinePolicies) => {
  if (!inlinePolicies) return 0;
  return Object.values(inlinePolicies).filter(policy => policy && policy.trim() !== '').length;
};

// Helper function to filter out empty inline policies
const filterNonEmptyInlinePolicies = (inlinePolicies) => {
  if (!inlinePolicies) return {};
  const filtered = {};
  Object.entries(inlinePolicies).forEach(([name, content]) => {
    if (content && content.trim() !== '') {
      filtered[name] = content;
    }
  });
  return filtered;
};

// --- Policy attachments -----------------------------------------------------
//
// A permission set can carry two kinds of customer policy, and they are not
// interchangeable:
//   * Prism-managed customer policies — Prism owns the document and
//     materialises it as `Prism-<Name>` in every account.
//   * Policy references — pointers to policies the CUSTOMER owns. Prism
//     attaches the real ARN by name and path, the document may differ per
//     account, and Prism never creates, edits or deletes it.
//
// Both live in ONE picker so there is exactly one place to attach either kind,
// and the picker is exported so the edit form in PermissionSetDetails renders
// the identical control — two definitions would let create and edit drift into
// disagreeing about what is attached.

// IAM caps a role at 10 managed policies. AWS-managed ARNs, Prism-managed
// customer policies and customer-owned policy references all occupy the same
// ten slots, so the counter has to add up all three kinds — the backend's
// EnforceAttachLimitWithRefs does exactly the same sum, and a role that exceeds
// it fails to provision in AWS rather than failing in Prism.
export const POLICY_ATTACH_LIMIT = 10;

export const attachSlotTotal = (awsCount = 0, cmpIds = [], refIds = []) =>
  awsCount + cmpIds.length + refIds.length;

export const isOverAttachLimit = (awsCount = 0, cmpIds = [], refIds = []) =>
  attachSlotTotal(awsCount, cmpIds, refIds) > POLICY_ATTACH_LIMIT;

// sameIdSet compares two id lists ignoring order. It is what makes the
// nil-vs-empty contract safe in the edit form: an attachment field is only ever
// put on the wire when the baseline the server reported and the admin's
// selection actually differ.
export const sameIdSet = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((id) => seen.has(id));
};

// List endpoints in this API are inconsistent about wrapping: some return a bare
// array, some `{ policies: [...] }`, some `{ data: ... }`. Normalising here keeps
// the picker working whichever shape the client method settles on.
const normalizeList = (payload, keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  if (payload.data && payload.data !== payload) return normalizeList(payload.data, keys);
  return [];
};

// PolicySlotMeter renders the live 10-slot budget shared by every attach surface.
export const PolicySlotMeter = ({ awsCount, managedCount, referenceCount }) => {
  const total = awsCount + managedCount + referenceCount;
  const over = total > POLICY_ATTACH_LIMIT;
  const barColor = over ? CK.red : total === POLICY_ATTACH_LIMIT ? CK.amber : CK.green;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', fontWeight: 600, color: CK.body }}>
          Managed policy slots
        </Typography>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', fontWeight: 700, color: over ? CK.red : CK.navy }}>
          {total} of {POLICY_ATTACH_LIMIT}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, (total / POLICY_ATTACH_LIMIT) * 100)}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: CK.pageBg,
          '& .MuiLinearProgress-bar': { backgroundColor: barColor, borderRadius: 3 },
        }}
      />
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
        <Chip size="small" label={`AWS-managed ${awsCount}`} sx={statusChipSx('info')} />
        <Chip size="small" label={`Prism-managed ${managedCount}`} sx={statusChipSx('success')} />
        <Chip size="small" label={`References ${referenceCount}`} sx={statusChipSx('purple')} />
      </Box>
    </Box>
  );
};

// PolicyPickerRow renders one selectable policy. The two kinds are colour-,
// icon- and chip-separated because attaching the wrong one has very different
// consequences: editing a Prism-managed policy rewrites it in every account,
// whereas a reference is a pointer Prism will never touch. A reference row
// deliberately offers no edit affordance and no link to a document — Prism owns
// neither.
const PolicyPickerRow = ({ item, selected, onToggle }) => {
  const isReference = item.kind === 'reference';
  const accent = isReference ? CK.purple : CK.blue;
  const Icon = isReference ? LinkIcon : PolicyIcon;

  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        p: 1,
        mb: 0.75,
        borderRadius: '6px',
        cursor: 'pointer',
        border: `1px solid ${selected ? `${accent}55` : CK.border}`,
        backgroundColor: selected ? `${accent}0F` : CK.surface,
        '&:hover': { backgroundColor: `${accent}14` },
      }}
    >
      <Checkbox
        checked={selected}
        onClick={(e) => e.stopPropagation()}
        onChange={onToggle}
        size="small"
        sx={{ p: 0.5, color: accent, '&.Mui-checked': { color: accent } }}
      />
      <Icon sx={{ color: accent, fontSize: 20, mt: 0.75 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.875rem', color: CK.navy, wordBreak: 'break-all' }}
          >
            {item.name}
          </Typography>
          <Chip
            size="small"
            label={isReference ? 'Reference · per account' : 'Prism-managed · one document'}
            sx={statusChipSx(isReference ? 'purple' : 'info')}
          />
        </Box>
        {item.secondary ? (
          <Typography
            sx={{
              fontFamily: isReference ? 'monospace' : FONT_BODY,
              fontSize: '0.75rem',
              color: CK.body,
              wordBreak: 'break-all',
            }}
          >
            {item.secondary}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

const referenceItem = (r) => ({
  kind: 'reference',
  id: r.id,
  name: r.policy_name || r.policyName,
  secondary: r.policy_path || r.policyPath || '/',
});

const managedItem = (c) => ({ kind: 'managed', id: c.id, name: c.name, secondary: c.description || '' });

// PolicyAttachmentPicker is the single attach surface for both kinds. It owns
// its libraries so create and edit can never show different catalogues, and is
// fully controlled: the parent holds the selection and therefore the payload.
//
// `attachedCmps` / `attachedReferences` are what the SERVER currently reports as
// attached (empty on create). An attached item missing from its library —
// deleted, beyond the first page, or the library failed to load — is still
// listed, so opening the picker can never make an admin silently drop an
// attachment they were never shown.
export const PolicyAttachmentPicker = ({
  awsManagedCount = 0,
  selectedCmpIds = [],
  selectedReferenceIds = [],
  onToggleCmp,
  onToggleReference,
  attachedCmps = [],
  attachedReferences = [],
}) => {
  const [cmpLibrary, setCmpLibrary] = useState([]);
  const [referenceLibrary, setReferenceLibrary] = useState([]);
  const [isLoadingLibraries, setIsLoadingLibraries] = useState(true);
  const [libraryError, setLibraryError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadLibraries = async () => {
      const failed = [];

      try {
        const res = await apiService.listCustomerManagedPolicies({ pageSize: 200 });
        if (mounted) setCmpLibrary(normalizeList(res, ['policies']));
      } catch (error) {
        console.error('Error fetching customer managed policies:', error);
        failed.push('Prism-managed policy library');
      }

      try {
        if (typeof apiService.getPolicyReferences !== 'function') {
          throw new Error('getPolicyReferences is not available on the API client');
        }
        // The list endpoint is not paginated and returns a bare array, so no
        // query parameters are sent.
        const res = await apiService.getPolicyReferences(getCustomerIdFromSubdomain());
        if (mounted) setReferenceLibrary(normalizeList(res, ['policy_references', 'policyReferences', 'references']));
      } catch (error) {
        console.error('Error fetching policy references:', error);
        failed.push('policy reference library');
      }

      if (!mounted) return;
      if (failed.length > 0) {
        setLibraryError(`Could not load the ${failed.join(' and the ')}. Selections already attached are still shown.`);
      }
      setIsLoadingLibraries(false);
    };

    loadLibraries();
    return () => {
      mounted = false;
    };
  }, []);

  const pickerGroups = useMemo(() => {
    const managed = cmpLibrary.map(managedItem);
    const references = referenceLibrary.map(referenceItem);

    const managedIds = new Set(managed.map((m) => m.id));
    attachedCmps.forEach((c) => {
      if (!managedIds.has(c.id)) managed.push(managedItem(c));
    });
    const referenceIds = new Set(references.map((r) => r.id));
    attachedReferences.forEach((r) => {
      if (!referenceIds.has(r.id)) references.push(referenceItem(r));
    });

    const query = search.trim().toLowerCase();
    const matches = (item) =>
      !query ||
      (item.name || '').toLowerCase().includes(query) ||
      (item.secondary || '').toLowerCase().includes(query);

    return {
      managed: managed.filter(matches),
      references: references.filter(matches),
      managedTotal: managed.length,
      referenceTotal: references.length,
    };
  }, [cmpLibrary, referenceLibrary, attachedCmps, attachedReferences, search]);

  const slotTotal = attachSlotTotal(awsManagedCount, selectedCmpIds, selectedReferenceIds);
  const overLimit = slotTotal > POLICY_ATTACH_LIMIT;

  return (
    <Box>
      <PolicySlotMeter
        awsCount={awsManagedCount}
        managedCount={selectedCmpIds.length}
        referenceCount={selectedReferenceIds.length}
      />

      {overLimit && (
        <Alert severity="error" sx={{ mt: 2, fontFamily: FONT_BODY }}>
          A role can carry at most {POLICY_ATTACH_LIMIT} managed policies. This selection is {awsManagedCount}{' '}
          AWS-managed + {selectedCmpIds.length} Prism-managed + {selectedReferenceIds.length} referenced = {slotTotal}.
          Remove {slotTotal - POLICY_ATTACH_LIMIT} before saving.
        </Alert>
      )}

      {libraryError && (
        <Alert severity="warning" sx={{ mt: 2, fontFamily: FONT_BODY }}>
          {libraryError}
        </Alert>
      )}

      <TextField
        size="small"
        fullWidth
        placeholder="Filter policies and references..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: CK.muted, fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mt: 2,
          '& .MuiOutlinedInput-root': {
            backgroundColor: CK.pageBg,
            borderRadius: '6px',
            fontFamily: FONT_BODY,
            fontSize: '0.875rem',
          },
        }}
      />

      {isLoadingLibraries ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ color: CK.green }} />
        </Box>
      ) : (
        <>
          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PolicyIcon sx={{ color: CK.blue, fontSize: 20 }} />
              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.9rem', color: CK.navy }}>
                Prism-managed customer policies ({selectedCmpIds.length})
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted, mb: 1, mt: 0.25 }}>
              Prism owns the document and materialises it as Prism-&lt;name&gt; in every account. Editing one changes
              every account that uses it.
            </Typography>
            {pickerGroups.managed.length === 0 ? (
              <Typography sx={{ ...emptyTextSx, fontSize: '0.8rem', fontStyle: 'italic' }}>
                {pickerGroups.managedTotal === 0
                  ? 'No customer-managed policies in the library yet. Create some from the sidebar.'
                  : 'No Prism-managed customer policies match.'}
              </Typography>
            ) : (
              pickerGroups.managed.map((item) => (
                <PolicyPickerRow
                  key={`managed-${item.id}`}
                  item={item}
                  selected={selectedCmpIds.includes(item.id)}
                  onToggle={() => onToggleCmp(item.id)}
                />
              ))
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon sx={{ color: CK.purple, fontSize: 20 }} />
              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.9rem', color: CK.navy }}>
                Customer-owned policy references ({selectedReferenceIds.length})
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted, mb: 1, mt: 0.25 }}>
              Pointers to policies your organisation already owns. Prism attaches the real ARN by name and path, so the
              policy must already exist in every target account and its document may differ per account. Prism never
              creates, edits or deletes it.
            </Typography>
            {pickerGroups.references.length === 0 ? (
              <Typography sx={{ ...emptyTextSx, fontSize: '0.8rem', fontStyle: 'italic' }}>
                {pickerGroups.referenceTotal === 0
                  ? 'No policy references registered yet. Add one from Customer Managed Policies in the sidebar.'
                  : 'No policy references match.'}
              </Typography>
            ) : (
              pickerGroups.references.map((item) => (
                <PolicyPickerRow
                  key={`reference-${item.id}`}
                  item={item}
                  selected={selectedReferenceIds.includes(item.id)}
                  onToggle={() => onToggleReference(item.id)}
                />
              ))
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

const PermissionSetForm = ({ awsManagedPolicies, onSubmit, fetchAwsPolicies }) => {
  const confirm = useConfirm();
  const [permissionSetForm, setPermissionSetForm] = useState({
    name: '',
    description: '',
    sessionDuration: 'PT12H',
    relayState: '',
    managedPolicies: [],
    inlinePolicies: {},
    customerManagedPolicyIds: [],
    policyReferenceIds: []
  });

  const [policySearchTerm, setPolicySearchTerm] = useState('');
  const [selectedPolicyCategory, setSelectedPolicyCategory] = useState('');
  const [policySortBy, setPolicySortBy] = useState('name');
  const [showPolicyPicker, setShowPolicyPicker] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [policiesPerPage] = useState(10);

  const [inlinePolicyName, setInlinePolicyName] = useState('');
  const [inlinePolicyContent, setInlinePolicyContent] = useState('');

  const [policyValidation, setPolicyValidation] = useState({
    isValidating: false,
    result: null,
    error: null
  });

  const [localPolicies, setLocalPolicies] = useState([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);

  const policiesFetched = useRef(false);

  useEffect(() => {
    if (isLoadingPolicies && awsManagedPolicies && awsManagedPolicies.length > 0) {
      setIsLoadingPolicies(false);
    }
  }, [awsManagedPolicies, isLoadingPolicies]);

  const debouncedValidatePolicy = useCallback(
    debounce((policyContent) => {
      if (!policyContent || policyContent.trim() === '') {
        setPolicyValidation({
          isValidating: false,
          result: null,
          error: null
        });
        return;
      }

      validatePolicy(policyContent);
    }, 800),
    []
  );

  useEffect(() => {
    if (inlinePolicyContent) {
      debouncedValidatePolicy(inlinePolicyContent);
    }

    return () => {
      debouncedValidatePolicy.cancel();
    };
  }, [inlinePolicyContent, debouncedValidatePolicy]);

  const validatePolicy = async (policyContent) => {
    if (!policyContent || policyContent.trim() === '') {
      return;
    }

    try {
      JSON.parse(policyContent);
    } catch (error) {
      setPolicyValidation({
        isValidating: false,
        result: null,
        error: "Invalid JSON format. Please check the syntax."
      });
      return;
    }

    setPolicyValidation({
      isValidating: true,
      result: null,
      error: null
    });

    try {
      const response = await apiService.validateIAMPolicy(policyContent);
      if (response.success) {
        setPolicyValidation({
          isValidating: false,
          result: response.data,
          error: null
        });
      } else {
        setPolicyValidation({
          isValidating: false,
          result: null,
          error: response.message || "Failed to validate policy"
        });
      }
    } catch (error) {
      console.error("Policy validation error:", error);
      setPolicyValidation({
        isValidating: false,
        result: null,
        error: error.message || "An error occurred during policy validation"
      });
    }
  };

  const availablePolicies = awsManagedPolicies && awsManagedPolicies.length > 0 ? awsManagedPolicies : localPolicies;

  const getPolicyCategories = () => {
    const categories = new Set();
    (availablePolicies || []).forEach(policy => {
      if (policy.name.includes('Amazon')) categories.add('Amazon Services');
      else if (policy.name.includes('AWS')) categories.add('AWS Services');
      else if (policy.name.includes('IAM') || policy.name.includes('Security')) categories.add('Security & IAM');
      else if (policy.name.includes('ReadOnly') || policy.name.includes('Read')) categories.add('Read Only');
      else if (policy.name.includes('Admin') || policy.name.includes('Full')) categories.add('Administrative');
      else if (policy.name.includes('Power') || policy.name.includes('Developer')) categories.add('Developer');
      else categories.add('Other');
    });
    return Array.from(categories).sort();
  };

  const getFilteredAndSortedPolicies = () => {
    let filtered = (availablePolicies || []).filter(policy => {
      const matchesSearch = !policySearchTerm ||
        policy.name.toLowerCase().includes(policySearchTerm.toLowerCase()) ||
        (policy.description && policy.description.toLowerCase().includes(policySearchTerm.toLowerCase())) ||
        policy.arn.toLowerCase().includes(policySearchTerm.toLowerCase());

      const matchesCategory = !selectedPolicyCategory ||
        (selectedPolicyCategory === 'Amazon Services' && policy.name.includes('Amazon')) ||
        (selectedPolicyCategory === 'AWS Services' && policy.name.includes('AWS') && !policy.name.includes('Amazon')) ||
        (selectedPolicyCategory === 'Security & IAM' && (policy.name.includes('IAM') || policy.name.includes('Security'))) ||
        (selectedPolicyCategory === 'Read Only' && (policy.name.includes('ReadOnly') || policy.name.includes('Read'))) ||
        (selectedPolicyCategory === 'Administrative' && (policy.name.includes('Admin') || policy.name.includes('Full'))) ||
        (selectedPolicyCategory === 'Developer' && (policy.name.includes('Power') || policy.name.includes('Developer'))) ||
        (selectedPolicyCategory === 'Other' && !policy.name.includes('Amazon') && !policy.name.includes('AWS') &&
         !policy.name.includes('IAM') && !policy.name.includes('Security') && !policy.name.includes('ReadOnly') &&
         !policy.name.includes('Read') && !policy.name.includes('Admin') && !policy.name.includes('Full') &&
         !policy.name.includes('Power') && !policy.name.includes('Developer'));

      return matchesSearch && matchesCategory;
    });

    filtered.sort((a, b) => {
      switch (policySortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'nameDesc':
          return b.name.localeCompare(a.name);
        case 'service':
          return (a.name.split(/[A-Z]/)[0] || '').localeCompare(b.name.split(/[A-Z]/)[0] || '');
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  };

  const filteredPolicies = getFilteredAndSortedPolicies();
  const totalPages = Math.ceil(filteredPolicies.length / policiesPerPage);
  const paginatedPolicies = filteredPolicies.slice(
    (currentPage - 1) * policiesPerPage,
    currentPage * policiesPerPage
  );

  const isOverPolicyLimit = isOverAttachLimit(
    permissionSetForm.managedPolicies.length,
    permissionSetForm.customerManagedPolicyIds,
    permissionSetForm.policyReferenceIds
  );

  const toggleCustomerManagedPolicy = (id) => {
    setPermissionSetForm((prev) => ({
      ...prev,
      customerManagedPolicyIds: prev.customerManagedPolicyIds.includes(id)
        ? prev.customerManagedPolicyIds.filter((x) => x !== id)
        : [...prev.customerManagedPolicyIds, id],
    }));
  };

  const togglePolicyReference = (id) => {
    setPermissionSetForm((prev) => ({
      ...prev,
      policyReferenceIds: prev.policyReferenceIds.includes(id)
        ? prev.policyReferenceIds.filter((x) => x !== id)
        : [...prev.policyReferenceIds, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // The submit button is disabled over the limit; this guard also covers a
    // form submitted by pressing Enter in a text field.
    if (isOverPolicyLimit) return;

    const apiRequest = {
      name: permissionSetForm.name,
      description: permissionSetForm.description,
      session_duration: permissionSetForm.sessionDuration,
      relay_state: permissionSetForm.relayState.trim(),
      managed_policies: permissionSetForm.managedPolicies,
      inline_policies: permissionSetForm.inlinePolicies
    };

    // NIL-VS-EMPTY: the backend reads a MISSING attachment key as "the caller
    // did not mention this kind — leave it alone" and a PRESENT-but-empty one
    // as "detach every one of them". A brand-new permission set has nothing to
    // detach, so an empty list carries no information and the key is omitted
    // rather than defaulted to `[]`. Never add a default here: the same payload
    // shape is what the edit form relies on, where a stray `[]` would silently
    // strip every attachment off an existing permission set.
    if (permissionSetForm.customerManagedPolicyIds.length > 0) {
      apiRequest.customer_managed_policy_ids = permissionSetForm.customerManagedPolicyIds;
    }
    if (permissionSetForm.policyReferenceIds.length > 0) {
      apiRequest.policy_reference_ids = permissionSetForm.policyReferenceIds;
    }

    const success = await onSubmit(apiRequest);
    if (success) {
      resetForm();
    }
  };

  const resetForm = () => {
    setPermissionSetForm({
      name: '',
      description: '',
      sessionDuration: 'PT12H',
      relayState: '',
      managedPolicies: [],
      inlinePolicies: {},
      customerManagedPolicyIds: [],
      policyReferenceIds: []
    });
    setInlinePolicyName('');
    setInlinePolicyContent('');
    setPolicySearchTerm('');
    setSelectedPolicyCategory('');
    setCurrentPage(1);
    setShowPolicyPicker(false);
  };

  const handleAddManagedPolicy = (policyArn) => {
    if (permissionSetForm.managedPolicies.includes(policyArn)) return;

    setPermissionSetForm({
      ...permissionSetForm,
      managedPolicies: [...permissionSetForm.managedPolicies, policyArn]
    });
  };

  const handleRemoveManagedPolicy = (policyArn) => {
    setPermissionSetForm({
      ...permissionSetForm,
      managedPolicies: permissionSetForm.managedPolicies.filter(arn => arn !== policyArn)
    });
  };

  const handleAddInlinePolicy = async () => {
    if (!inlinePolicyName || !inlinePolicyContent.trim()) return;

    try {
      JSON.parse(inlinePolicyContent);

      if (policyValidation.error || (policyValidation.result && policyValidation.result.findings &&
          policyValidation.result.findings.some(f => f.findingType === 'ERROR'))) {
        const confirmed = await confirm({
          title: 'Policy Validation Errors',
          message: 'The policy has validation errors. Are you sure you want to add it anyway?',
          confirmText: 'Add Anyway',
          variant: 'warning',
        });
        if (!confirmed) {
          return;
        }
      }

      setPermissionSetForm({
        ...permissionSetForm,
        inlinePolicies: {
          ...permissionSetForm.inlinePolicies,
          [inlinePolicyName]: inlinePolicyContent
        }
      });

      setInlinePolicyName('');
      setInlinePolicyContent('');
      setPolicyValidation({
        isValidating: false,
        result: null,
        error: null
      });
    } catch (error) {
      alert('Invalid JSON format for inline policy');
    }
  };

  const handleRemoveInlinePolicy = (policyName) => {
    const newInlinePolicies = { ...permissionSetForm.inlinePolicies };
    delete newInlinePolicies[policyName];

    setPermissionSetForm({
      ...permissionSetForm,
      inlinePolicies: newInlinePolicies
    });
  };

  const renderValidationResults = () => {
    if (policyValidation.isValidating) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#F39C12' }}>
          <CircularProgress size={14} sx={{ color: '#F39C12' }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Validating policy...
          </Typography>
        </Box>
      );
    }

    if (policyValidation.error) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#E74C3C' }}>
          <ErrorIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            {policyValidation.error}
          </Typography>
        </Box>
      );
    }

    if (!policyValidation.result) {
      return null;
    }

    const findings = policyValidation.result.findings || [];

    if (findings.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: '#27AE60' }}>
          <CheckCircleIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Policy is valid
          </Typography>
        </Box>
      );
    }

    const errors = findings.filter(f => f.findingType === 'ERROR');
    const warnings = findings.filter(f => f.findingType === 'WARNING');
    const suggestions = findings.filter(f => f.findingType === 'SUGGESTION');

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#F39C12' }}>
          <WarningIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
            Policy validation found issues
          </Typography>
        </Box>

        {errors.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#FDF2F2', borderRadius: '6px', border: '1px solid #E74C3C20' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#E74C3C', mb: 0.5 }}>
              Errors ({errors.length})
            </Typography>
            {errors.map((error, index) => (
              <Typography key={`error-${index}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666666', fontWeight: 500 }}>
                • <strong>{error.issueCode || 'Error'}:</strong> {error.findingDetails || 'Invalid policy'}
              </Typography>
            ))}
          </Box>
        )}

        {warnings.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#FEF9E7', borderRadius: '6px', border: '1px solid #F39C1220' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#F39C12', mb: 0.5 }}>
              Warnings ({warnings.length})
            </Typography>
            {warnings.map((warning, index) => (
              <Typography key={`warning-${index}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666666', fontWeight: 500 }}>
                • <strong>{warning.issueCode || 'Warning'}:</strong> {warning.findingDetails || 'Policy warning'}
              </Typography>
            ))}
          </Box>
        )}

        {suggestions.length > 0 && (
          <Box sx={{ p: 1.5, backgroundColor: '#EBF5FB', borderRadius: '6px', border: '1px solid #4698D320' }}>
            <Typography sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#4698D3', mb: 0.5 }}>
              Suggestions ({suggestions.length})
            </Typography>
            {suggestions.map((suggestion, index) => (
              <Typography key={`suggestion-${index}`} sx={{ fontSize: '0.75rem', fontFamily: 'Metropolis, sans-serif', color: '#666666', fontWeight: 500 }}>
                • <strong>{suggestion.issueCode || 'Suggestion'}:</strong> {suggestion.findingDetails || 'Policy suggestion'}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const handleShowPolicyPicker = async () => {
    if (!showPolicyPicker) {
      if (availablePolicies.length === 0) {
        setIsLoadingPolicies(true);
        try {
          const policies = await fetchAwsPolicies();

          if (policies && Array.isArray(policies) && policies.length > 0) {
            setLocalPolicies(policies);
          }

          setTimeout(() => {
            setIsLoadingPolicies(false);
          }, 100);
        } catch (error) {
          console.error('Failed to fetch AWS policies:', error);
          setIsLoadingPolicies(false);
        }
      }
    }
    setShowPolicyPicker(!showPolicyPicker);
  };

  // Minimalist input style
  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Metropolis, sans-serif',
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: '#27AE60',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#27AE60',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
      '&.Mui-focused': {
        color: '#27AE60',
      },
    },
  };

  return (
    <motion.div variants={containerVariants} initial="initial" animate="animate" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
      {/* Header */}
      <motion.div variants={itemVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#F0F9F4',
          borderRadius: '12px 12px 0 0',
          p: 3,
          borderBottom: '1px solid #E5E5E5',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              backgroundColor: '#27AE60',
              width: 48,
              height: 48,
            }}
          >
            <SecurityIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                fontSize: '1.5rem',
                color: '#253E66',
              }}
            >
              Create Permission Set
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                fontSize: '0.875rem',
                color: '#666666',
                fontWeight: 500,
              }}
            >
              Define AWS permissions and access policies
            </Typography>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Form Content */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '0 0 12px 12px',
          p: 3,
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          {/* Basic Information */}
          <motion.div variants={itemVariants}>
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#253E66',
              mb: 2,
            }}
          >
            Basic Information
          </Typography>

          <TextField
            fullWidth
            label="Permission Set Name"
            value={permissionSetForm.name}
            onChange={(e) => setPermissionSetForm({ ...permissionSetForm, name: filterInput(e.target.value, 'GENERAL_TEXT') })}
            required
            placeholder="Enter permission set name"
            sx={{ ...inputStyle, mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description"
            value={permissionSetForm.description}
            onChange={(e) => setPermissionSetForm({ ...permissionSetForm, description: filterInput(e.target.value, 'GENERAL_TEXT') })}
            multiline
            rows={3}
            placeholder="Describe the purpose of this permission set"
            sx={{ ...inputStyle, mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel sx={{ fontFamily: 'Metropolis, sans-serif', color: '#666666', '&.Mui-focused': { color: '#27AE60' } }}>
              Session Duration
            </InputLabel>
            <Select
              value={permissionSetForm.sessionDuration}
              onChange={(e) => setPermissionSetForm({ ...permissionSetForm, sessionDuration: e.target.value })}
              label="Session Duration"
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                backgroundColor: '#FFFFFF',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E5E5E5',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#27AE60',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#27AE60',
                },
              }}
            >
              <MenuItem value="PT1H">1 Hour</MenuItem>
              <MenuItem value="PT2H">2 Hours</MenuItem>
              <MenuItem value="PT4H">4 Hours</MenuItem>
              <MenuItem value="PT8H">8 Hours</MenuItem>
              <MenuItem value="PT12H">12 Hours</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Relay state — optional"
            value={permissionSetForm.relayState}
            onChange={(e) => setPermissionSetForm({ ...permissionSetForm, relayState: e.target.value.slice(0, 320) })}
            placeholder="https://console.aws.amazon.com/ec2/v2/home?region=us-east-1"
            helperText={`Destination URL after AWS console federation. Up to 320 characters; alphanumeric, spaces, and & $ @ : / ? # = + - _ . ~ { }. (${permissionSetForm.relayState.length}/320)`}
            inputProps={{ maxLength: 320 }}
            sx={{ ...inputStyle, mb: 3 }}
          />
          </motion.div>

          {/* AWS Managed Policies Section */}
          <motion.div variants={itemVariants} style={{ marginBottom: 24 }}>
          <Accordion
            defaultExpanded
            sx={{
              backgroundColor: '#F9FBFD',
              border: '1px solid #E5E5E5',
              borderRadius: '6px !important',
              '&:before': { display: 'none' },
              boxShadow: 'none',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: '#666666' }} />}
              sx={{
                '& .MuiAccordionSummary-content': { my: 1.5 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PolicyIcon sx={{ color: '#27AE60', fontSize: 22 }} />
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  AWS Managed Policies ({permissionSetForm.managedPolicies.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {/* Selected Policies */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500, mb: 1 }}>
                  Selected Policies
                </Typography>
                {permissionSetForm.managedPolicies.length === 0 ? (
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#999999', fontStyle: 'italic', fontWeight: 500 }}>
                    No policies selected
                  </Typography>
                ) : (
                  <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '6px', border: '1px solid #E5E5E5', p: 1 }}>
                    {permissionSetForm.managedPolicies.map(policyArn => {
                      const policyDetails = (availablePolicies || []).find(p => p.arn === policyArn) || { name: 'Unknown Policy' };
                      return (
                        <Box key={policyArn} sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          px: 1.5,
                          mb: 0.5,
                          backgroundColor: '#F0F9F4',
                          borderRadius: '4px',
                          '&:last-child': { mb: 0 }
                        }}>
                          <Box>
                            <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#253E66', fontWeight: 600 }}>
                              {policyDetails.name}
                            </Typography>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#999999' }}>
                              {policyArn}
                            </Typography>
                          </Box>
                          <IconButton
                            onClick={() => handleRemoveManagedPolicy(policyArn)}
                            size="small"
                            sx={{ color: '#E74C3C' }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              <Button
                variant="outlined"
                onClick={handleShowPolicyPicker}
                disabled={isLoadingPolicies}
                startIcon={isLoadingPolicies ? <CircularProgress size={16} /> : <AddIcon />}
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderColor: '#E5E5E5',
                  color: '#666666',
                  mb: 2,
                  '&:hover': {
                    borderColor: '#27AE60',
                    backgroundColor: '#F0F9F4',
                    color: '#27AE60',
                  },
                }}
              >
                {isLoadingPolicies ? 'Loading Policies...' : showPolicyPicker ? 'Hide Policy Browser' : 'Browse & Add Policies'}
              </Button>

              {/* Policy Picker */}
              {showPolicyPicker && (
                <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '6px', border: '1px solid #E5E5E5', p: 2 }}>
                  {isLoadingPolicies ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress size={24} sx={{ color: '#27AE60', mb: 2 }} />
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                        Fetching AWS managed policies...
                      </Typography>
                    </Box>
                  ) : (!availablePolicies || availablePolicies.length === 0) ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                        No AWS managed policies available.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          setIsLoadingPolicies(true);
                          try {
                            const policies = await fetchAwsPolicies();
                            if (policies && Array.isArray(policies) && policies.length > 0) {
                              setLocalPolicies(policies);
                            }
                          } catch (error) {
                            console.error('Retry fetch failed:', error);
                          } finally {
                            setIsLoadingPolicies(false);
                          }
                        }}
                        sx={{
                          mt: 2,
                          fontFamily: 'Metropolis, sans-serif',
                          textTransform: 'none',
                          borderColor: '#E5E5E5',
                        }}
                      >
                        Retry Loading Policies
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          placeholder="Search policies by name, description, or ARN..."
                          value={policySearchTerm}
                          onChange={(e) => {
                            setPolicySearchTerm(e.target.value);
                            setCurrentPage(1);
                          }}
                          size="small"
                          sx={{ ...inputStyle, mb: 2 }}
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}>Category</InputLabel>
                            <Select
                              value={selectedPolicyCategory}
                              onChange={(e) => {
                                setSelectedPolicyCategory(e.target.value);
                                setCurrentPage(1);
                              }}
                              label="Category"
                              sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}
                            >
                              <MenuItem value="">All Categories</MenuItem>
                              {getPolicyCategories().map(category => (
                                <MenuItem key={category} value={category}>{category}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}>Sort</InputLabel>
                            <Select
                              value={policySortBy}
                              onChange={(e) => setPolicySortBy(e.target.value)}
                              label="Sort"
                              sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem' }}
                            >
                              <MenuItem value="name">Sort: A-Z</MenuItem>
                              <MenuItem value="nameDesc">Sort: Z-A</MenuItem>
                              <MenuItem value="service">Sort: Service</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>

                      <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.8rem', color: '#666666', fontWeight: 500, mb: 1.5 }}>
                        Showing {paginatedPolicies.length} of {filteredPolicies.length} policies
                        {policySearchTerm && ` matching "${policySearchTerm}"`}
                        {selectedPolicyCategory && ` in ${selectedPolicyCategory}`}
                      </Typography>

                      <Box sx={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        border: '1px solid #E5E5E5',
                        borderRadius: '6px',
                      }}>
                        {paginatedPolicies.map(policy => {
                          const isSelected = permissionSetForm.managedPolicies.includes(policy.arn);
                          return (
                            <Box key={policy.arn} sx={{
                              p: 1.5,
                              borderBottom: '1px solid #E5E5E5',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              backgroundColor: isSelected ? '#F0F9F4' : '#FFFFFF',
                              '&:hover': {
                                backgroundColor: isSelected ? '#E8F6EC' : '#F9FBFD',
                              },
                              '&:last-child': { borderBottom: 'none' },
                            }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#253E66', fontWeight: 600 }}>
                                  {policy.name}
                                </Typography>
                                {policy.description && (
                                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem', color: '#666666', fontWeight: 500 }}>
                                    {policy.description}
                                  </Typography>
                                )}
                                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#999999', mt: 0.25 }}>
                                  {policy.arn}
                                </Typography>
                              </Box>
                              <Button
                                variant={isSelected ? "contained" : "outlined"}
                                size="small"
                                onClick={() => isSelected ? handleRemoveManagedPolicy(policy.arn) : handleAddManagedPolicy(policy.arn)}
                                sx={{
                                  ml: 2,
                                  minWidth: '80px',
                                  fontFamily: 'Metropolis, sans-serif',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  ...(isSelected ? {
                                    backgroundColor: '#27AE60',
                                    '&:hover': { backgroundColor: '#219A52' },
                                  } : {
                                    borderColor: '#E5E5E5',
                                    color: '#666666',
                                    '&:hover': { borderColor: '#27AE60', color: '#27AE60' },
                                  }),
                                }}
                              >
                                {isSelected ? 'Selected' : '+ Add'}
                              </Button>
                            </Box>
                          );
                        })}
                      </Box>

                      {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
                          <Button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none' }}
                          >
                            Previous
                          </Button>
                          <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                            Page {currentPage} of {totalPages}
                          </Typography>
                          <Button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none' }}
                          >
                            Next
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
          </motion.div>

          {/* Inline Policies Section */}
          <motion.div variants={itemVariants}>
          <Accordion
            sx={{
              backgroundColor: '#F9FBFD',
              border: '1px solid #E5E5E5',
              borderRadius: '6px !important',
              mb: 3,
              '&:before': { display: 'none' },
              boxShadow: 'none',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: '#666666' }} />}
              sx={{
                '& .MuiAccordionSummary-content': { my: 1.5 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CodeIcon sx={{ color: '#4698D3', fontSize: 22 }} />
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Inline Policies ({countInlinePolicies(permissionSetForm.inlinePolicies)})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {/* Add New Inline Policy */}
              <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '6px', border: '1px solid #E5E5E5', p: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Policy Name"
                  placeholder="Enter policy name"
                  value={inlinePolicyName}
                  onChange={(e) => setInlinePolicyName(filterInput(e.target.value, 'IAM_POLICY_NAME'))}
                  helperText="Only letters, numbers, and + = , . @ _ - are allowed (no spaces)"
                  inputProps={{ maxLength: 128 }}
                  size="small"
                  sx={{ ...inputStyle, mb: 2 }}
                />

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500 }}>
                      Policy JSON
                    </Typography>
                    {policyValidation.isValidating && (
                      <Chip
                        label="Validating..."
                        size="small"
                        sx={{ backgroundColor: '#FEF9E7', color: '#F39C12', fontFamily: 'Metropolis, sans-serif', fontSize: '0.7rem' }}
                      />
                    )}
                    {!policyValidation.isValidating && policyValidation.result && !policyValidation.error &&
                      !policyValidation.result.findings?.some(f => f.findingType === 'ERROR') && (
                      <Chip
                        label="Valid"
                        size="small"
                        icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                        sx={{ backgroundColor: '#F0F9F4', color: '#27AE60', fontFamily: 'Metropolis, sans-serif', fontSize: '0.7rem' }}
                      />
                    )}
                    <PolicyGeneratorLink sx={{ ml: 'auto' }} />
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    placeholder="Enter policy JSON"
                    value={inlinePolicyContent}
                    onChange={(e) => setInlinePolicyContent(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderColor: policyValidation.error || (policyValidation.result?.findings?.some(f => f.findingType === 'ERROR'))
                            ? '#E74C3C'
                            : '#E5E5E5',
                        },
                        '&:hover fieldset': {
                          borderColor: '#27AE60',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#27AE60',
                        },
                      },
                    }}
                  />
                  {renderValidationResults()}
                </Box>

                <Button
                  variant="contained"
                  onClick={handleAddInlinePolicy}
                  disabled={!inlinePolicyName || !inlinePolicyContent || policyValidation.isValidating}
                  startIcon={<AddIcon />}
                  sx={{
                    mt: 2,
                    backgroundColor: '#27AE60',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#219A52',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#E5E5E5',
                      color: '#999999',
                    },
                  }}
                >
                  Add Policy
                </Button>
              </Box>

              {/* Existing Inline Policies */}
              {countInlinePolicies(permissionSetForm.inlinePolicies) > 0 && (
                <Box>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.85rem', color: '#666666', fontWeight: 500, mb: 1 }}>
                    Current Inline Policies ({countInlinePolicies(permissionSetForm.inlinePolicies)})
                  </Typography>
                  {Object.entries(filterNonEmptyInlinePolicies(permissionSetForm.inlinePolicies)).map(([name, content]) => (
                    <Box key={name} sx={{
                      backgroundColor: '#FFFFFF',
                      borderRadius: '6px',
                      border: '1px solid #E5E5E5',
                      p: 2,
                      mb: 1,
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.9rem', color: '#253E66', fontWeight: 600 }}>
                          {name}
                        </Typography>
                        <IconButton
                          onClick={() => handleRemoveInlinePolicy(name)}
                          size="small"
                          sx={{ color: '#E74C3C' }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{
                        backgroundColor: '#F9FBFD',
                        borderRadius: '4px',
                        p: 1.5,
                        maxHeight: '150px',
                        overflow: 'auto',
                        border: '1px solid #E5E5E5',
                      }}>
                        <Typography
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            color: '#666666',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0
                          }}
                        >
                          {previewPolicyJson(content)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
          </motion.div>

          {/* Policy Attachments Section — Prism-managed customer policies and
              customer-owned references share one picker and one slot budget. */}
          <motion.div variants={itemVariants}>
          <Accordion
            defaultExpanded
            sx={{
              mt: 1,
              boxShadow: 'none',
              border: '1px solid #E5E5E5',
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              mb: 3,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: '#F4F8FB',
                borderRadius: '8px',
                '& .MuiAccordionSummary-content': { my: 1.5 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PolicyIcon sx={{ color: '#4698D3', fontSize: 22 }} />
                <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Policy Attachments (
                  {permissionSetForm.customerManagedPolicyIds.length + permissionSetForm.policyReferenceIds.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <PolicyAttachmentPicker
                awsManagedCount={permissionSetForm.managedPolicies.length}
                selectedCmpIds={permissionSetForm.customerManagedPolicyIds}
                selectedReferenceIds={permissionSetForm.policyReferenceIds}
                onToggleCmp={toggleCustomerManagedPolicy}
                onToggleReference={togglePolicyReference}
              />
            </AccordionDetails>
          </Accordion>
          </motion.div>

          {/* Submit Button */}
          <motion.div variants={itemVariants}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isOverPolicyLimit}
              sx={{
                backgroundColor: '#27AE60',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#219A52',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#E5E5E5',
                  color: '#999999',
                },
              }}
            >
              Create Permission Set
            </Button>
          </Box>
          </motion.div>
        </Box>
      </Box>
    </motion.div>
  );
};

export default PermissionSetForm;
